import { state, FREE_MODE_LIMIT } from '../shared/state';
import { $, showToast } from '../shared/index';
import { sessionStore } from './session-store';
import { renderDocChip, updateContextBadge, updateTokenInfo } from '../shared/ui';
import { registerAttachmentRemover } from '../shared/app-hooks';
import { showRegistrationPrompt } from '../shared/navigation';
import { refreshSessionIndicators } from './session-indicators';
import type { AttachedDoc } from '../shared/types';

const isGuestMode = () => localStorage.getItem('avgexpert_guest_mode') === '1';


const POLL_INTERVAL_MS = 2000;
const pollTimers = new Map<string, ReturnType<typeof setTimeout>>();

function pollKey(sessionId: string, docId: string) {
  return `${sessionId}:${docId}`;
}

function authHeaders(): Record<string, string> {
  return { Authorization: 'Bearer ' + (state.authToken || 'null') };
}

export async function ensureConversationSessionId(sessionId?: string): Promise<string | null> {
  if (!state.authToken && !isGuestMode()) return null;

  const id = sessionId || sessionStore.activeSessionId || crypto.randomUUID();
  const session = sessionStore.getOrCreate(id);
  sessionStore.setActiveSession(id);

  const activeCategory = $<HTMLSelectElement>('conversation-session-category')?.value
    || state.currentUser?.category
    || null;

  await fetch('/api/sessions', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id,
      title: session.title || 'Новый разговор',
      messages: session.history || [],
      category: activeCategory,
      updatedAt: Date.now(),
    }),
  });

  return id;
}

function stopPolling(sessionId: string, docId: string): void {
  const key = pollKey(sessionId, docId);
  const timer = pollTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    pollTimers.delete(key);
  }
}

function statusLabel(status: string | undefined): string {
  if (status === 'pending' || status === 'processing') return 'индексируется…';
  if (status === 'ready') return 'готов';
  if (status === 'failed') return 'ошибка';
  return status || '';
}

function updateDocChipForSession(sessionId: string, doc: AttachedDoc, index: number): void {
  if (sessionStore.activeSessionId !== sessionId) return;
  const container = document.getElementById('attached-docs');
  if (!container) return;
  const chips = container.querySelectorAll('.doc-chip');
  const chip = chips[index];
  if (!chip) return;
  const label = statusLabel(doc.status);
  const statusClass = doc.status === 'ready' ? 'ready' : (doc.status === 'failed' ? 'failed' : 'pending');
  chip.innerHTML = `<span>📎 ${doc.name} <span class="doc-chip-status doc-chip-status--${statusClass}">${label}</span></span>
                    <button class="remove-doc-btn" data-index="${index}">×</button>`;
  chip.querySelector('.remove-doc-btn')?.addEventListener('click', () => removeSessionAttachment(sessionId, index));
}

export async function removeSessionAttachment(sessionId: string, index: number): Promise<void> {
  const session = sessionStore.getSession(sessionId);
  if (!session) return;
  const doc = session.attachedDocs[index];
  if (doc?.id) {
    stopPolling(sessionId, doc.id);
    try {
      await fetch(`/api/conversation/sessions/${sessionId}/attachments/${doc.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
    } catch (err) {
      console.error('Failed to delete session attachment', err);
    }
  }
  session.attachedDocs.splice(index, 1);
  session.dirty = true;

  if (sessionStore.activeSessionId === sessionId) {
    const container = document.getElementById('attached-docs');
    if (container) {
      container.textContent = '';
      session.attachedDocs.forEach((d, i) => renderDocChip(d, i, sessionId));
    }
  }
  updateTokenInfo();
  updateContextBadge();
  refreshSessionIndicators();
}

async function pollAttachmentStatus(sessionId: string, docId: string, index: number): Promise<void> {
  try {
    const res = await fetch(`/api/conversation/sessions/${sessionId}/attachments/${docId}`, {
      headers: authHeaders(),
      cache: 'no-store',
    });
    if (!res.ok) return;
    const data = await res.json();
    const session = sessionStore.getSession(sessionId);
    const doc = session?.attachedDocs[index];
    if (!doc || doc.id !== docId) return;

    doc.status = data.status;
    if (data.status === 'ready') {
      doc.indexed = true;
      stopPolling(sessionId, docId);
      if (sessionStore.activeSessionId === sessionId) {
        showToast(`Документ «${doc.name}» проиндексирован`, 'success');
      }
    } else if (data.status === 'failed') {
      stopPolling(sessionId, docId);
      if (sessionStore.activeSessionId === sessionId) {
        showToast(`Не удалось проиндексировать «${doc.name}»`, 'error');
      }
    } else {
      const timer = setTimeout(() => pollAttachmentStatus(sessionId, docId, index), POLL_INTERVAL_MS);
      pollTimers.set(pollKey(sessionId, docId), timer);
    }
    updateDocChipForSession(sessionId, doc, index);
    updateContextBadge();
    refreshSessionIndicators();
  } catch (err) {
    console.error('Attachment poll failed', err);
  }
}

export function hasPendingAttachments(sessionId?: string | null): boolean {
  const check = (docs: AttachedDoc[]) =>
    docs.some((d) => d.id && (d.status === 'pending' || d.status === 'processing'));

  if (sessionId) {
    const session = sessionStore.getSession(sessionId);
    return session ? check(session.attachedDocs) : false;
  }
  const active = sessionStore.getActiveSession();
  return active ? check(active.attachedDocs) : false;
}

export async function uploadSessionAttachment(sessionId: string, file: File, content: string): Promise<boolean> {
  if (!state.authToken && !isGuestMode()) return false;

  if (isGuestMode()) {
    const activityCount = parseInt(localStorage.getItem('avgexpert_free_activity_count') || '0', 10);
    if (activityCount >= FREE_MODE_LIMIT) {
      showToast(`Вы исчерпали лимит бесплатных запросов (максимум ${FREE_MODE_LIMIT}). Пожалуйста, зарегистрируйтесь.`, 'error');
      showRegistrationPrompt();
      return false;
    }
  }

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!['txt', 'md', 'markdown', 'pdf', 'docx'].includes(ext || '')) {
    showToast('Поддерживаются .txt, .md, .pdf и .docx', 'error');
    return false;
  }

  const session = sessionStore.getOrCreate(sessionId);
  if (session.attachedDocs.length >= state.maxDocsAllowed) {
    showToast(`Максимум ${state.maxDocsAllowed} документов для вашей категории`);
    return false;
  }

  const resolvedSessionId = await ensureConversationSessionId(sessionId);
  if (!resolvedSessionId) return false;

  const res = await fetch(`/api/conversation/sessions/${resolvedSessionId}/attachments`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, content }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    showToast(data.detail || 'Не удалось загрузить вложение', 'error');
    return false;
  }

  if (isGuestMode()) {
    const activityCount = parseInt(localStorage.getItem('avgexpert_free_activity_count') || '0', 10);
    localStorage.setItem('avgexpert_free_activity_count', String(activityCount + 1));
  }

  const index = session.attachedDocs.length;
  session.attachedDocs.push({
    id: data.id,
    name: data.filename || file.name,
    status: data.status || 'pending',
    indexed: false,
    sessionScoped: true,
  });
  session.dirty = true;

  if (sessionStore.activeSessionId === resolvedSessionId) {
    renderDocChip(session.attachedDocs[index], index, resolvedSessionId);
  }
  updateTokenInfo();
  updateContextBadge();
  refreshSessionIndicators();

  if (data.status === 'pending' || data.status === 'processing') {
    const timer = setTimeout(() => pollAttachmentStatus(resolvedSessionId, data.id, index), POLL_INTERVAL_MS);
    pollTimers.set(pollKey(resolvedSessionId, data.id), timer);
  }

  return true;
}

export async function restoreSessionAttachments(sessionId: string): Promise<void> {
  if (!state.authToken && !isGuestMode()) return;
  try {
    const res = await fetch(`/api/conversation/sessions/${sessionId}/attachments`, {
      headers: authHeaders(),
      cache: 'no-store',
    });
    if (!res.ok) return;
    const data = await res.json() as { attachments?: Array<{ id: string; filename: string; status: string }> };
    const session = sessionStore.getOrCreate(sessionId);
    session.attachedDocs = (data.attachments || []).map((a) => ({
      id: a.id,
      name: a.filename,
      status: a.status,
      indexed: a.status === 'ready',
      sessionScoped: true,
    }));

    if (sessionStore.activeSessionId === sessionId) {
      const container = document.getElementById('attached-docs');
      if (container) {
        container.textContent = '';
        session.attachedDocs.forEach((d, i) => renderDocChip(d, i, sessionId));
      }
    }

    session.attachedDocs.forEach((doc, index) => {
      if (doc.id && (doc.status === 'pending' || doc.status === 'processing')) {
        const timer = setTimeout(() => pollAttachmentStatus(sessionId, doc.id!, index), POLL_INTERVAL_MS);
        pollTimers.set(pollKey(sessionId, doc.id!), timer);
      }
    });

    refreshSessionIndicators();
  } catch (err) {
    console.error('Failed to restore session attachments', err);
  }
}

export function stopAllAttachmentPolling(): void {
  for (const key of pollTimers.keys()) {
    const timer = pollTimers.get(key);
    if (timer) clearTimeout(timer);
    pollTimers.delete(key);
  }
}

export function getAttachmentStatusLabel(status: string | undefined): string {
  return statusLabel(status);
}

// shared/ui removes doc chips of session-scoped attachments through this hook
// instead of importing the session feature (ADR-002 phase C inversion).
registerAttachmentRemover((sessionId, index) => {
  void removeSessionAttachment(sessionId, index);
});
